import {
  DIR_DRAW_BEND_THRESHOLD,
  DIR_DRAW_MAX_BENDS,
  DIR_DRAW_MIN_LENGTH,
  DIR_DRAW_ARROW_SIZE,
  DIRECTIONS,
} from '../../../../const'
import { DirectionStateManager } from './index'
import { DirectionLineType, Vector2d } from '../../../../types'
import type { IArrow, IGroup, ILayer, ILine, IRenderer } from '../../../../backend/IRenderer'

// 방향도형 선분에서 벌룬과 겹치는 구간을 잘라내기 위한 클립 정보
export interface DirectionBalloonClip {
  halfSize: number
  isCircle: boolean
}

/**
 * 선분에서 벌룬 내부와 겹치는 구간을 제거한 부분 선분들을 반환합니다.
 * 벌룬 채움이 투명일 때 연결선이 벌룬 내부를 통과해 보이는 것을 방지하며,
 * 벌룬이 꺾임점을 덮어 선분이 벌룬을 관통하는 경우 선분을 둘로 분할합니다.
 * @returns 그릴 부분 선분들의 points 배열 (선분 전체가 벌룬에 덮이면 빈 배열)
 */
function clipSegmentByBalloon(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  centerX: number,
  centerY: number,
  clip: DirectionBalloonClip,
  lineStrokeWidth: number,
): number[][] {
  const full = [[startX, startY, endX, endY]]
  const dx = endX - startX
  const dy = endY - startY
  const lengthSq = dx * dx + dy * dy
  if (lengthSq <= 0) return []

  // lineCap square가 끝점 너머로 선굵기 절반만큼 뻗으므로 그만큼 여유를 둠
  const half = clip.halfSize + lineStrokeWidth / 2

  // 선분 파라미터 t(0~1) 기준 벌룬 내부 구간 [tEnter, tExit] 계산
  let tEnter: number
  let tExit: number

  if (clip.isCircle) {
    // 원과 선분의 교차 구간
    const fx = startX - centerX
    const fy = startY - centerY
    const b = 2 * (fx * dx + fy * dy)
    const c = fx * fx + fy * fy - half * half
    const disc = b * b - 4 * lengthSq * c
    if (disc <= 0) return full
    const sqrtDisc = Math.sqrt(disc)
    tEnter = (-b - sqrtDisc) / (2 * lengthSq)
    tExit = (-b + sqrtDisc) / (2 * lengthSq)
  } else {
    // 축 정렬 사각 벌룬과 선분의 교차 구간 (Liang-Barsky)
    tEnter = -Infinity
    tExit = Infinity

    if (dx === 0) {
      if (Math.abs(startX - centerX) > half) return full
    } else {
      const ta = (centerX - half - startX) / dx
      const tb = (centerX + half - startX) / dx
      tEnter = Math.max(tEnter, Math.min(ta, tb))
      tExit = Math.min(tExit, Math.max(ta, tb))
    }

    if (dy === 0) {
      if (Math.abs(startY - centerY) > half) return full
    } else {
      const ta = (centerY - half - startY) / dy
      const tb = (centerY + half - startY) / dy
      tEnter = Math.max(tEnter, Math.min(ta, tb))
      tExit = Math.min(tExit, Math.max(ta, tb))
    }

    if (tEnter > tExit) return full
  }

  const eps = 1e-6
  const insideStart = Math.max(tEnter, 0)
  const insideEnd = Math.min(tExit, 1)
  if (insideEnd <= insideStart) return full // 교차 구간이 선분 범위 밖

  const result: number[][] = []
  if (insideStart > eps) {
    result.push([startX, startY, startX + dx * insideStart, startY + dy * insideStart])
  }
  if (insideEnd < 1 - eps) {
    result.push([startX + dx * insideEnd, startY + dy * insideEnd, endX, endY])
  }
  return result
}

// 방향도형 시각적 업데이트 함수
export function updateDirectionElements(
  renderer: IRenderer,
  directionShapeGroup: IGroup,
  arrowPoints: number[],
  color: string,
  strokeWidth: number,
  arrowSize: number,
  originalArrowHead?: {
    startX: number
    startY: number
    direction: { x: number; y: number }
  } | null,
  balloonClip?: DirectionBalloonClip | null,
): void {
  if (!directionShapeGroup || !arrowPoints || arrowPoints.length < 4) return

  // 기존 모든 자식 요소들 제거하고 새로 생성
  directionShapeGroup.destroyChildren()

  // 화살표 머리 정보 계산
  let headStartX: number, headStartY: number, headDirection: { x: number; y: number }

  if (originalArrowHead) {
    headStartX = originalArrowHead.startX
    headStartY = originalArrowHead.startY
    headDirection = originalArrowHead.direction
  } else {
    headStartX = arrowPoints[0]
    headStartY = arrowPoints[1]
    const secondX = arrowPoints[2]
    const secondY = arrowPoints[3]

    const dx = secondX - headStartX
    const dy = secondY - headStartY
    const length = Math.sqrt(dx * dx + dy * dy)

    headDirection = length > 0 ? { x: dx / length, y: dy / length } : { x: 0, y: -1 }
  }

  // 화살표 머리 생성
  const arrowEndX = headStartX + headDirection.x * arrowSize
  const arrowEndY = headStartY + headDirection.y * arrowSize

  const arrowHead = renderer.createArrow({
    points: [arrowEndX, arrowEndY, headStartX, headStartY],
    pointerLength: arrowSize,
    pointerWidth: arrowSize,
    fill: color,
    stroke: color,
    strokeWidth: 1, // 화살표 테두리는 항상 1픽셀로 고정
    listening: false,
    hitArea: { kind: 'strokeWidth', px: (strokeWidth || 0) + 50 },
    perfectDraw: false,
  })
  directionShapeGroup.add(arrowHead)

  // 벌룬 중심 (경로 끝점에 벌룬이 고정됨)
  const balloonCenterX = arrowPoints[arrowPoints.length - 2]
  const balloonCenterY = arrowPoints[arrowPoints.length - 1]

  // 선분들 생성
  for (let i = 0; i < arrowPoints.length - 2; i += 2) {
    let startX = arrowPoints[i]
    let startY = arrowPoints[i + 1]
    const endX = arrowPoints[i + 2]
    const endY = arrowPoints[i + 3]

    // 첫 번째 선분의 경우 화살표 머리와 겹치지 않도록 시작점을 앞으로 이동
    if (i === 0) {
      // 선분의 방향 벡터 계산
      const dx = endX - startX
      const dy = endY - startY
      const length = Math.sqrt(dx * dx + dy * dy)

      if (length > arrowSize) {
        const dirX = dx / length
        const dirY = dy / length

        // 시작점을 화살표 머리 크기만큼 앞으로 이동
        startX = startX + dirX * arrowSize
        startY = startY + dirY * arrowSize
      }
    }

    // 벌룬과 겹치는 구간은 잘라내어 투명 벌룬을 통과해 보이지 않도록 처리
    // (벌룬이 꺾임점을 덮는 경우 관통하는 선분은 둘로 분할됨)
    const segments = balloonClip
      ? clipSegmentByBalloon(
          startX,
          startY,
          endX,
          endY,
          balloonCenterX,
          balloonCenterY,
          balloonClip,
          strokeWidth,
        )
      : [[startX, startY, endX, endY]]

    for (const segmentPoints of segments) {
      const line = renderer.createLine({
        points: segmentPoints,
        stroke: color,
        strokeWidth,
        lineCap: 'square',
        listening: false,
        hitArea: { kind: 'strokeWidth', px: (strokeWidth || 0) + 50 },
        perfectDraw: false,
      })
      directionShapeGroup.add(line)
    }
  }

  // 화살표 머리를 맨 앞으로
  arrowHead.moveToTop()

  // 화살표 머리 잘림 방지를 위한 invisible padding rectangle 추가 - 더 큰 여백 확보
  const allX = [headStartX, arrowEndX, ...arrowPoints.filter((_, i) => i % 2 === 0)]
  const allY = [headStartY, arrowEndY, ...arrowPoints.filter((_, i) => i % 2 === 1)]

  // 방향에 따른 추가 패딩 (희한하게 위쪽 보고있으면 화살표 귀 잘림)
  const extraPadding = arrowSize * 6 // 기본 6배
  let leftPadding = extraPadding
  let rightPadding = extraPadding
  const topPadding = extraPadding
  const bottomPadding = extraPadding

  // 북쪽 방향 (headDirection.y < -0.5)인 경우 좌우 패딩 추가 확장
  if (headDirection.y < -0.5) {
    leftPadding = arrowSize * 8
    rightPadding = arrowSize * 8
  }

  const minX = Math.min(...allX) - leftPadding
  const maxX = Math.max(...allX) + rightPadding
  const minY = Math.min(...allY) - topPadding
  const maxY = Math.max(...allY) + bottomPadding

  const paddingRect = renderer.createRect({
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    fill: 'transparent',
    stroke: 'transparent',
    listening: false,
    visible: false,
  })
  directionShapeGroup.add(paddingRect)
  paddingRect.moveToBottom() // 맨 아래로 이동

  // 여분 패딩 더 추가함
  const extraArrowPadding = renderer.createRect({
    x: headStartX - arrowSize * 4,
    y: headStartY - arrowSize * 4,
    width: arrowSize * 8,
    height: arrowSize * 8,
    fill: 'transparent',
    stroke: 'transparent',
    listening: false,
    visible: false,
  })
  directionShapeGroup.add(extraArrowPadding)
  extraArrowPadding.moveToBottom()
}

// 방향도형 상태 초기화 함수
export function initializeDirectionDragState(
  stateManager: DirectionStateManager,
  points: number[],
): void {
  if (!points || points.length < 4) return

  const startX = points[0]
  const startY = points[1]
  const secondX = points[2]
  const secondY = points[3]
  const dx = secondX - startX
  const dy = secondY - startY
  const length = Math.sqrt(dx * dx + dy * dy)

  stateManager.fixedDirection = snapToDirections(dx, dy)

  if (length > 0) {
    const snappedDirection = stateManager.fixedDirection as Vector2d & { index: number }
    stateManager.originalArrowHead = {
      startX,
      startY,
      direction: { x: snappedDirection.x, y: snappedDirection.y },
    }
  }

  // 기존 경로의 모든 선분 정보를 복원
  stateManager.fixedLines = []
  for (let i = 0; i < points.length - 2; i += 2) {
    const segStart = { x: points[i], y: points[i + 1] }
    const segEnd = { x: points[i + 2], y: points[i + 3] }

    let nextDirection
    // 다음 꺾인 점이 있는지 확인하여 다음 선분의 방향을 설정
    if (i + 4 < points.length) {
      const nextSegEnd = { x: points[i + 4], y: points[i + 5] }
      nextDirection = snapToDirections(nextSegEnd.x - segEnd.x, nextSegEnd.y - segEnd.y)
    } else {
      // 마지막 선분인 경우 자신의 방향을 유지
      nextDirection = snapToDirections(segEnd.x - segStart.x, segEnd.y - segStart.y)
    }

    stateManager.fixedLines.push({
      start: segStart,
      end: segEnd,
      direction: nextDirection,
    })
  }

  // 마지막 선분을 활성 상태로 설정
  stateManager.activeLineIndex = Math.max(0, stateManager.fixedLines.length - 1)
  stateManager.isDirectionFixed = true
}

// tempArrow 생성 및 관리 함수
export function createAndManageTempArrow(
  renderer: IRenderer,
  stateManager: DirectionStateManager,
  dragPos: { x: number; y: number },
  points: number[],
  config: {
    color: string
    strokeWidth: number
    systemLayer: ILayer
  },
): { direction: Vector2d & { index: number }; distance: number } {
  const { color, strokeWidth, systemLayer } = config

  // 시작점 계산
  let startX: number, startY: number
  if (stateManager.originalArrowHead) {
    startX = stateManager.originalArrowHead.startX
    startY = stateManager.originalArrowHead.startY
  } else {
    startX = points[0]
    startY = points[1]
  }

  const distance = Math.sqrt((dragPos.x - startX) ** 2 + (dragPos.y - startY) ** 2)
  const dx = dragPos.x - startX
  const dy = dragPos.y - startY
  const tempDirection = snapToDirections(dx, dy)

  // 방향이 바뀌면 저장
  if (!stateManager.lastDirection || stateManager.lastDirection.index !== tempDirection.index) {
    stateManager.lastDirection = tempDirection
  }

  // tempArrow 생성 또는 업데이트
  if (!stateManager.tempArrow) {
    stateManager.tempArrow = createTempArrow(renderer, tempDirection, {
      startX,
      startY,
      color,
      strokeWidth,
    })
    systemLayer.add(stateManager.tempArrow)
  } else {
    updateTempArrow(stateManager.tempArrow, startX, startY, tempDirection, distance)
  }

  return { direction: tempDirection, distance }
}

// 벌룬 위치 업데이트 함수
export function updateDirectionBalloonPosition(
  /* eslint-disable @typescript-eslint/no-explicit-any */
  balloon: any,
  balloonDragDiff: any,
  /* eslint-enable @typescript-eslint/no-explicit-any */
  points: number[],
  entityX: number,
  entityY: number,
  isTempArrowMode: boolean = false,
  tempPosition?: { x: number; y: number },
): void {
  if (!balloon) return

  if (isTempArrowMode && tempPosition) {
    // tempArrow 모드에서는 임시 위치 사용
    balloon.setPosition(tempPosition)
    balloon.head.rect.opacity(0.5)
    balloon.head.text.opacity(0.5)
  } else if (points.length >= 4) {
    // 일반 모드에서는 끝점 사용
    const endX = points[points.length - 2]
    const endY = points[points.length - 1]
    balloon.setPosition({ x: endX, y: endY })
    balloon.head.rect.opacity(1.0)
    balloon.head.text.opacity(1.0)

    // balloonDragDiff 업데이트
    if (balloonDragDiff) {
      const pos = balloon.getPosition()
      balloonDragDiff.x = pos.x - entityX
      balloonDragDiff.y = pos.y - entityY
    }
  }
}

// 방향도형 방향성 결정 함수
export function snapToDirections(dx: number, dy: number): Vector2d & { index: number } {
  if (dx === 0 && dy === 0) return { x: 1, y: 0, index: 2 }

  const length = Math.sqrt(dx * dx + dy * dy)
  const normalizedX = dx / length
  const normalizedY = dy / length

  let bestIndex = 0
  let bestDot = -2

  DIRECTIONS.forEach((dir, index) => {
    const dot = normalizedX * dir.x + normalizedY * dir.y
    if (dot > bestDot) {
      bestDot = dot
      bestIndex = index
    }
  })
  const bestDirection = DIRECTIONS[bestIndex]
  return { ...bestDirection, index: bestIndex }
}

// 방향도형 경로 계산 함수
export function calculateDirectionPath(
  startX: number,
  startY: number,
  mouseX: number,
  mouseY: number,
  fixedDirection: Vector2d & { index: number },
  fixedLines: DirectionLineType[] = [],
  activeLineIndex: number = 0,
  scale: number = 1,
): {
  path: Vector2d[]
  newFixedLines: DirectionLineType[]
  newActiveIndex: number
} {
  // 상수들
  const factor = 1.5
  const minLength = DIR_DRAW_MIN_LENGTH * (1 + (factor - 1) * (1 / scale - 1)) // 현재 줌레벨에 따른 최소 길이 보정
  const maxBends = DIR_DRAW_MAX_BENDS
  const bendThreshold = DIR_DRAW_BEND_THRESHOLD

  // 현재 선분
  const currentLineIndex = fixedLines.length // 순번
  let activeStartPoint: Vector2d // 시작점
  let activeDirection: Vector2d & { index: number } // 방향성

  if (currentLineIndex === 0) {
    // 1번선은 시작점과 방향이 고정
    activeStartPoint = { x: startX, y: startY }
    activeDirection = fixedDirection
  } else {
    // 그 이후는 직전 선분의 끝점과 방향성
    activeStartPoint = fixedLines[currentLineIndex - 1].end
    activeDirection = fixedLines[currentLineIndex - 1].direction
  }

  const dx = mouseX - activeStartPoint.x
  const dy = mouseY - activeStartPoint.y
  const projectedLength = dx * activeDirection.x + dy * activeDirection.y

  // 현재 선분의 끝점(꺾이려는 지점) 계산
  const currentLength = Math.max(minLength, projectedLength)
  const currentEndPoint = {
    x: activeStartPoint.x + activeDirection.x * currentLength,
    y: activeStartPoint.y + activeDirection.y * currentLength,
  }

  // 꺾이려는 지점에서 마우스까지의 실제 거리 계산 (꺾임 판정용)
  const bendDx = mouseX - currentEndPoint.x
  const bendDy = mouseY - currentEndPoint.y
  const actualDistanceFromBendPoint = Math.sqrt(bendDx * bendDx + bendDy * bendDy)

  // 현재 선분의 길이가 최소 길이보다 짧고, 이전 선분이 있다면 꺾은점을 제거
  if (projectedLength < minLength && currentLineIndex > 0) {
    const newFixedLines = fixedLines.slice(0, -1)
    const newActiveIndex = currentLineIndex - 1

    const newActiveStartPoint =
      newActiveIndex === 0 ? { x: startX, y: startY } : newFixedLines[newActiveIndex - 1].end
    const newActiveDirection =
      newActiveIndex === 0 ? fixedDirection : newFixedLines[newActiveIndex - 1].direction

    const newDx = mouseX - newActiveStartPoint.x
    const newDy = mouseY - newActiveStartPoint.y
    const newProjectedLength = Math.max(
      minLength,
      newDx * newActiveDirection.x + newDy * newActiveDirection.y,
    )

    const resultPoints: Vector2d[] = [{ x: startX, y: startY }]
    for (let i = 0; i < newFixedLines.length; i++) {
      resultPoints.push(newFixedLines[i].end)
    }
    resultPoints.push({
      x: newActiveStartPoint.x + newActiveDirection.x * newProjectedLength,
      y: newActiveStartPoint.y + newActiveDirection.y * newProjectedLength,
    })

    return { path: resultPoints, newFixedLines: newFixedLines, newActiveIndex }
  }

  let angleInDegrees = 0
  const shouldCheckAngle = currentLineIndex < maxBends

  // 더 꺾을 수 있으면 (최대 꺾은점 갯수가 안되었으면)
  if (shouldCheckAngle) {
    const mouseDirection = snapToDirections(bendDx, bendDy)
    const angleDiff = Math.abs(activeDirection.index - mouseDirection.index)
    const minAngleDiff = Math.min(angleDiff, 8 - angleDiff)
    angleInDegrees = minAngleDiff * 45

    // 꺾임 판정: 실제 거리와 각도 모두 고려
    if (angleInDegrees >= bendThreshold && actualDistanceFromBendPoint >= minLength) {
      // 각도 임계값 이상이고 실제 거리가 최소 길이 이상이면 꺾음
      const bendPoint = {
        x: activeStartPoint.x + activeDirection.x * currentLength,
        y: activeStartPoint.y + activeDirection.y * currentLength,
      }

      let newDirection: Vector2d & { index: number }
      if (angleInDegrees === 90) {
        newDirection = mouseDirection
      } else {
        // 90도 방향 계산
        const clockwise90 = (activeDirection.index + 2) & 7
        const counterclockwise90 = (activeDirection.index - 2 + 8) & 7

        const clockwiseDir = DIRECTIONS[clockwise90]
        const counterclockwiseDir = DIRECTIONS[counterclockwise90]

        // 내적 계산
        const clockwiseDot = mouseDirection.x * clockwiseDir.x + mouseDirection.y * clockwiseDir.y
        const counterclockwiseDot =
          mouseDirection.x * counterclockwiseDir.x + mouseDirection.y * counterclockwiseDir.y

        newDirection =
          clockwiseDot > counterclockwiseDot
            ? { x: clockwiseDir.x, y: clockwiseDir.y, index: clockwise90 }
            : { x: counterclockwiseDir.x, y: counterclockwiseDir.y, index: counterclockwise90 }
      }

      const newFixedLines = new Array(fixedLines.length + 1)
      for (let i = 0; i < fixedLines.length; i++) {
        newFixedLines[i] = fixedLines[i]
      }
      newFixedLines[fixedLines.length] = {
        start: activeStartPoint,
        end: bendPoint,
        direction: newDirection,
      }

      // 새 선 길이 계산
      const newDx = mouseX - bendPoint.x
      const newDy = mouseY - bendPoint.y
      const newProjectedLength = Math.max(
        minLength,
        newDx * newDirection.x + newDy * newDirection.y,
      )

      // 결과 점 배열
      const resultPoints: Vector2d[] = new Array(currentLineIndex + 3)
      resultPoints[0] = { x: startX, y: startY }
      for (let i = 0; i < currentLineIndex; i++) {
        resultPoints[i + 1] = fixedLines[i].end
      }
      resultPoints[currentLineIndex + 1] = bendPoint
      resultPoints[currentLineIndex + 2] = {
        x: bendPoint.x + newDirection.x * newProjectedLength,
        y: bendPoint.y + newDirection.y * newProjectedLength,
      }

      return {
        path: resultPoints,
        newFixedLines,
        newActiveIndex: currentLineIndex + 1,
      }
    }
  }

  // 결과 점 배열
  const resultPoints: Vector2d[] = new Array(currentLineIndex + 2)
  resultPoints[0] = { x: startX, y: startY }

  // 배열 복사
  for (let i = 0; i < currentLineIndex; i++) {
    resultPoints[i + 1] = fixedLines[i].end
  }

  resultPoints[currentLineIndex + 1] = {
    x: activeStartPoint.x + activeDirection.x * currentLength,
    y: activeStartPoint.y + activeDirection.y * currentLength,
  }

  return { path: resultPoints, newFixedLines: fixedLines, newActiveIndex: activeLineIndex }
}

// 방향도형 렌더링 함수
export function updateDirectionRender(
  renderer: IRenderer,
  path: Vector2d[],
  lines: ILine[],
  arrow: IArrow | undefined,
  fixedDirection: (Vector2d & { index: number }) | null,
  shapeConfg: {
    layer: ILayer
    startX: number
    startY: number
    color: string
    strokeWidth: number
  },
): void {
  const { layer, startX, startY, color, strokeWidth } = shapeConfg
  // 기존 라인들 숨기기
  lines.forEach(line => line.visible(false))

  // 경로를 선분들로 그리기 (성능 최적화)
  for (let i = 0; i < path.length - 1; i++) {
    if (i >= lines.length) {
      const line = renderer.createLine({
        stroke: color,
        strokeWidth,
        lineCap: 'square',
        listening: false,
        perfectDraw: false, // 성능 향상을 위해 비활성화
        shadowEnabled: false, // 그림자 비활성화
      })
      lines.push(line)
      layer.add(line)
    }

    const line = lines[i]

    // 색상과 굵기 업데이트 (기존 라인 재사용 시)
    if (line.stroke() !== color) line.stroke(color)
    if (line.strokeWidth() !== strokeWidth) line.strokeWidth(strokeWidth)

    let startX = path[i].x
    let startY = path[i].y
    const endX = path[i + 1].x
    const endY = path[i + 1].y

    // 첫 번째 선분의 경우 화살표 머리와 겹치지 않도록 시작점을 앞으로 이동
    if (i === 0) {
      // 선분의 방향 벡터 계산
      const dx = endX - startX
      const dy = endY - startY
      const length = Math.sqrt(dx * dx + dy * dy)

      if (length > DIR_DRAW_ARROW_SIZE) {
        const dirX = dx / length
        const dirY = dy / length

        // 시작점을 화살표 머리 크기만큼 앞으로 이동
        startX = startX + dirX * DIR_DRAW_ARROW_SIZE
        startY = startY + dirY * DIR_DRAW_ARROW_SIZE
      }
    }

    // 보정된 좌표로 선분 그리기 (기존 points와 비교하여 변경된 경우만 업데이트)
    const currentPoints = line.points()
    const newPoints = [startX, startY, endX, endY]

    // 점이 변경된 경우만 업데이트 (성능 최적화)
    if (
      currentPoints.length !== 4 ||
      Math.abs(currentPoints[0] - newPoints[0]) > 0.1 ||
      Math.abs(currentPoints[1] - newPoints[1]) > 0.1 ||
      Math.abs(currentPoints[2] - newPoints[2]) > 0.1 ||
      Math.abs(currentPoints[3] - newPoints[3]) > 0.1
    ) {
      line.points(newPoints)
    }

    if (!line.visible()) line.visible(true)
  }

  // 화살표 머리
  if (arrow && fixedDirection) {
    const endX = path.length >= 2 ? path[1].x : startX + fixedDirection.x * DIR_DRAW_ARROW_SIZE * 4
    const endY = path.length >= 2 ? path[1].y : startY + fixedDirection.y * DIR_DRAW_ARROW_SIZE * 4

    // 화살표머리는 시작점과 끝점이 반대임.. 좁은쪽이 3,4번째 (실제 화살표와 일관되게)
    arrow.points([endX, endY, startX, startY])
    arrow.visible(true)
  }
}

// 임시 화살표 생성 함수
export function createTempArrow(
  renderer: IRenderer,
  direction: Vector2d & { index: number },
  shapeConfig: {
    startX: number
    startY: number
    color: string
    strokeWidth: number
  },
): IArrow {
  const { startX, startY, color } = shapeConfig
  const endX = startX + direction.x * DIR_DRAW_ARROW_SIZE * 4
  const endY = startY + direction.y * DIR_DRAW_ARROW_SIZE * 4

  return renderer.createArrow({
    points: [endX, endY, startX, startY], // 화살표 머리는 시작-끝이 반대, 실제 화살표와 동일한 시작점
    pointerLength: DIR_DRAW_ARROW_SIZE,
    pointerWidth: DIR_DRAW_ARROW_SIZE,
    fill: color,
    stroke: color,
    strokeWidth: 1, // 화살표 테두리는 항상 1픽셀로 고정
    listening: false,
    dash: [4, 6],
    opacity: 0.4,
  })
}

// 방향 고정 뒤 실제 화살표 머리 생성 함수 (handleController 방향 그리기 미리보기)
export function createDirectionHeadArrow(renderer: IRenderer, color: string): IArrow {
  return renderer.createArrow({
    points: [0, 0, 0, 0],
    pointerLength: DIR_DRAW_ARROW_SIZE,
    pointerWidth: DIR_DRAW_ARROW_SIZE,
    fill: color,
    stroke: color,
    strokeWidth: 1, // 화살표 테두리는 항상 1픽셀로 고정
    listening: false,
  })
}

// 임시 화살표 업데이트 함수
export function updateTempArrow(
  tempArrow: IArrow,
  startX: number,
  startY: number,
  direction: Vector2d & { index: number },
  distance: number,
): void {
  const tempLength = Math.max(distance, DIR_DRAW_ARROW_SIZE * 4)

  // 임시 화살표 끝점 계산
  const endX = startX + direction.x * tempLength
  const endY = startY + direction.y * tempLength

  // 실제 화살표와 동일한 시작점 사용
  tempArrow.points([endX, endY, startX, startY])
}
